import { PlusOutlined } from "@ant-design/icons"
import { Button, Flex, Table, Typography } from "antd"
import { EntityTable } from "../../../../../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../../../../../components/EntityTable/surfaces.ts"
import type { BaseOptionType } from "antd/lib/select"
import React, { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { DATE_FORMAT, getNow } from "../../../../../../../utils/date/date.ts"
import {
  getReceivablePayableTotals,
  getTypeSummary
} from "../../../../../../../utils/finance-summary/finance-summary.ts"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import type { FinancialTransactionItem } from "../../../../../types/project-finance.types.tsx"
import type {
  ProjectMilestone,
  ProjectMilestoneFinanceItem
} from "../../../../../types/project-phases.types.tsx"
import {
  FINANCE_ITEM_STATUSES,
  FINANCE_ITEM_TYPES
} from "../../../../constants/finance-item-constants.ts"
import { ProjectViewMilestoneFinanceItemRow } from "./ProjectViewMilestoneFinanceItemRow.tsx"
import { getColumns } from "./columns.tsx"

const NEW_ROW_ID = "__new_transaction__"

interface ProjectViewMilestoneFinanceItemsProps {
  userId: string
  milestone: ProjectMilestone
  projectId: string
  options: Record<string, BaseOptionType[]>
  //
  editAllowed?: boolean
  deleteAllowed?: boolean
  createAllowed?: boolean
  deleteFileAllowed?: boolean
  uploadFileAllowed?: boolean
  deleteForeignFileAllowed?: boolean
  //
  onAdd: (
    data: Partial<ProjectMilestoneFinanceItem>,
    milestone: ProjectMilestone
  ) => Promise<boolean>
  onUpdate: (
    itemId: string,
    data: Partial<ProjectMilestoneFinanceItem>,
    milestone: ProjectMilestone
  ) => Promise<void>
  onDelete: (id: string, milestone: ProjectMilestone) => Promise<void>
  loading?: boolean
  disabled: boolean
}

export function ProjectViewMilestoneFinanceItems({
  userId,
  milestone,
  projectId,
  options,
  //
  editAllowed,
  deleteAllowed,
  createAllowed,
  deleteFileAllowed,
  uploadFileAllowed,
  deleteForeignFileAllowed,
  //
  onAdd,
  onUpdate,
  onDelete,
  disabled,
  loading = false
}: ProjectViewMilestoneFinanceItemsProps) {
  const { t } = useTranslation()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [isAdding, setIsAdding] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const isBusy = !!deletingId || loading
  const isLocked = !!editingId || isAdding

  const dataSource = useMemo(() => {
    const items = milestone?.financial_items || []
    if (!isAdding) return items

    const date = getNow().format(DATE_FORMAT)

    const newItem = {
      id: NEW_ROW_ID,
      date,
      due_date: date,
      item_type: FINANCE_ITEM_TYPES?.PAYABLE,
      status: FINANCE_ITEM_STATUSES.PLANNED
    } as FinancialTransactionItem

    return [newItem, ...items]
  }, [milestone?.financial_items, isAdding])

  const onSelectRow = useCallback(
    async (record: Partial<ProjectMilestoneFinanceItem>, action: string) => {
      switch (action) {
        case "create": {
          const success = await onAdd(
            {
              ...record,
              project_phase: milestone?.id
            },
            milestone
          )
          if (success) setIsAdding(false)
          break
        }
        case "update":
          if (editingId) {
            await onUpdate(editingId, record, milestone)
            setEditingId(null)
          }
          break
        case "cancel":
          return setEditingId(null)
        case "cancel_create":
          return setIsAdding(false)
      }
    },
    [editingId, milestone, onAdd, onUpdate]
  )

  const onEdit = (record: ProjectMilestoneFinanceItem) => {
    setEditingId(record.id)
    setIsAdding(false)
  }

  const onDeleteHandler = async (id: string) => {
    try {
      setDeletingId(id)
      await onDelete(id, milestone)
    } catch (error) {
      console.error("Delete failed:", error)
    } finally {
      setDeletingId(null)
    }
  }

  const onAddNewRow = () => {
    setIsAdding(true)
    setEditingId(null)
  }

  const columns = useMemo(
    () =>
      getColumns({
        t,
        currentUserId: userId,
        editAllowed,
        deleteAllowed,
        deleteFileAllowed,
        uploadFileAllowed,
        deleteForeignFileAllowed,
        disabled: disabled || isBusy,
        onEdit,
        projectId,
        onDelete: onDeleteHandler,
        deletingId,
        isAdding,
        editingId
      }),
    [
      deletingId,
      disabled,
      editingId,
      isAdding,
      isBusy,
      onDeleteHandler,
      projectId,
      createAllowed,
      editAllowed,
      deleteAllowed,
      deleteFileAllowed,
      uploadFileAllowed,
      deleteForeignFileAllowed,
      userId,
      t
    ]
  )

  const Row = useCallback(
    (
      props: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLTableRowElement>,
        HTMLTableRowElement
      >
    ) => {
      const rowKey = (props as { ["data-row-key"]?: string })[
        "data-row-key"
      ] as string | undefined
      const record = rowKey
        ? dataSource.find((item) => item.id === rowKey)
        : null

      if (!record?.id) return null

      const isEditing = record && editingId === record.id
      const isCreating = record?.id === NEW_ROW_ID

      if (!isEditing && !isCreating) {
        return <tr {...props} />
      }

      return (
        <ProjectViewMilestoneFinanceItemRow
          {...props}
          record={record}
          options={options}
          mode={isCreating ? "create" : "edit"}
          onSelectRow={onSelectRow}
        />
      )
    },
    [dataSource, editingId, onSelectRow, options]
  )

  return (
    /* No `selectionMode="on-demand"`: ProjectViewMilestoneFinanceItemRow emits
       a fixed list of `<td>`s and never reads `children`, so its edit row
       would not gain the checkbox cell the header and data rows do — every
       field would sit one column left of its heading and the pinned Actions
       cell would land under the wrong column. `lockColumnLayout` does not
       help: it pins order and visibility, not the selection column. Port the
       row to `buildEditRowCells`, then opt in. */
    <EntityTable<ProjectMilestoneFinanceItem>
      surface={TABLE_SURFACES.milestoneFinanceItems}
      tab={DEFAULT_TAB}
      lockColumnLayout
      rowKey="id"
      columns={columns}
      dataSource={dataSource}
      withOutPagination
      pagination={false}
      scroll={{ x: "max-content" }}
      size="small"
      className={"m-0 [&_.ant-table]:m-0"}
      loading={loading}
      onRow={(record) => ({
        onDoubleClick: editAllowed
          ? () => !isLocked && onEdit(record)
          : undefined
      })}
      components={editAllowed || createAllowed ? { body: { row: Row } } : {}}
      summary={(data) => {
        const rows = [...data]
        const typeSummary = getTypeSummary(rows)
        const { receivable, payable } = getReceivablePayableTotals(rows)

        return (
          <Table.Summary>
            <Table.Summary.Row className="bg-primary/10 ">
              {typeSummary.length > 0 ? (
                <>
                  <Table.Summary.Cell index={0} colSpan={4}>
                    <Typography.Text strong>Summary</Typography.Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell
                    index={1}
                    align="right"
                    className={"pr-0"}
                  >
                    <Flex vertical>
                      <Typography.Text>Receivable:</Typography.Text>
                      <Typography.Text>Payable:</Typography.Text>
                    </Flex>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="left" className={"pl-1"}>
                    <Flex vertical>
                      <Typography.Text
                        strong
                        ellipsis={{ tooltip: budgetFormat(receivable) }}
                      >
                        {budgetFormat(receivable)}
                      </Typography.Text>
                      <Typography.Text
                        strong
                        ellipsis={{ tooltip: budgetFormat(payable) }}
                      >
                        {budgetFormat(payable)}
                      </Typography.Text>
                    </Flex>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} colSpan={6} />
                </>
              ) : (
                <>
                  <Table.Summary.Cell index={0} colSpan={5}>
                    Summary
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="left">
                    {budgetFormat(0)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} colSpan={10} />
                </>
              )}
            </Table.Summary.Row>
          </Table.Summary>
        )
      }}
      footer={() =>
        createAllowed && !isAdding ? (
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={onAddNewRow}
            disabled={disabled || isBusy || !!editingId}
            block
            data-guide-target="milestone-financial-items-add"
          >
            Item
          </Button>
        ) : null
      }
    />
  )
}
